/*
-------------------------------------------------------------------------------------
Authors         : Alinsonorin, John Myl B., Awi, Joseph Kahl L., Gozon, Daniel Allan
Date Created    : April 30, 2025
Folder          : src/components/homepage/index
File            : Homepage.tsx
Description     : 
    This component allows the user to access the homepage of the web app.
Copyright © 2025. All rights reserved.
-------------------------------------------------------------------------------------
*/



// Import React Systems
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, signOut } from "firebase/auth";

// Import MUI Components
import {
  Box,
  Grid,
  Stack,
  Typography,
  Card,
  CardContent,
  Rating,
  CircularProgress
} from "@mui/material"

// Import Atoms
import ButtonComp from "../../../atoms/button/Button"
import Text from "../../../atoms/text/Text"

// Import Functions
import { fetchUserDetails } from "../../../../functions/fetchUserDetails"
import { getFirestore, doc, getDoc } from "firebase/firestore"
import app from "../../../../../backend/firebaseConfig"

const Hero = ({
  index,
  title,
  subtitle
}) => {

  const navigate = useNavigate();
  const auth = getAuth(app);
  const db = getFirestore(app);
  const [userDetails, setUserDetails] = useState(null);
  const [isScheduleLoading, setIsScheduleLoading] = useState(false);
  const [isPortfolioLoading, setIsPortfolioLoading] = useState(false);

  // UseEffects
  useEffect(() => {
    const details = fetchUserDetails(setUserDetails);
    return () => {
      details && details();
    };
  }, []);

  // Check if user is clinic personnel
  const isClinicPersonnel = userDetails?.role === "DentistOwner" || 
                            userDetails?.role === "AssociateDentist" || 
                            userDetails?.role === "ClinicStaff";

  // Button click handlers
  const handleScheduleAppointment = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setIsScheduleLoading(true);
    
    // If clinic personnel, navigate to manage appointment page
    if (isClinicPersonnel) {
      setIsScheduleLoading(false);
      navigate("/manage-appointment");
      return;
    }
    
    // Check if user is authenticated
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      // Not signed in - redirect to sign-in
      setIsScheduleLoading(false);
      navigate("/sign-in", { 
        state: { 
          from: "/patient-appointment-booking",
          message: "Please sign in to schedule an appointment"
        } 
      });
      return;
    }
    
    // Check email verification status before navigating
    try {
      // Check Firebase Auth email verification (source of truth)
      if (!currentUser.emailVerified) {
        // Email not verified - sign out and redirect to sign-in
        setIsScheduleLoading(false);
        try {
          await signOut(auth);
        } catch (error) {
          console.error("Error signing out:", error);
        }
        navigate("/sign-in", {
          state: {
            from: "/patient-appointment-booking",
            message: "Please verify your email address before scheduling an appointment. Check your inbox for the verification link."
          }
        });
        return;
      }
      
      // Also check Firestore for mobile-only users
      const userRef = doc(db, "users", currentUser.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const isMobileOnly = userData.emailVerified === "MobileOnly";
        const emailVerified = userData.emailVerified === "Yes";
        const isMobileEmail = currentUser.email?.includes("@mobileuser.dentalapp.com");
        
        // If mobile-only or email is verified, proceed to booking
        if (!isMobileOnly && !emailVerified && !isMobileEmail) {
          // Email not verified in Firestore either - sign out and redirect to sign-in
          setIsScheduleLoading(false);
          try {
            await signOut(auth);
          } catch (error) {
            console.error("Error signing out:", error);
          }
          navigate("/sign-in", {
            state: {
              from: "/patient-appointment-booking",
              message: "Please verify your email address before scheduling an appointment. Check your inbox for the verification link."
            }
          });
          return;
        }
      }
      
      // Email is verified - navigate to appointment booking
      setIsScheduleLoading(false);
      navigate("/patient-appointment-booking");
    } catch (error) {
      console.error("Error checking email verification:", error);
      // On error, proceed to booking and let ProtectedRoute handle it
      setIsScheduleLoading(false);
      navigate("/patient-appointment-booking");
    }
  };

  const handleViewPortfolio = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsPortfolioLoading(true);
    navigate("/about");
    setIsPortfolioLoading(false);
  };

  const gridStylesContainer = {
    backgroundColor: 
      index !== true ? "#2C251E" : 
      "transparent",
    padding: {
      xs: "80px 20px 80px 20px", 
      sm: "80px 30px 80px 30px", 
      md: "80px 110px 80px 110px", 
      lg: "80px 110px 80px 110px", 
      xl: "80px 110px 80px 110px" 
    },
    width: "100%",
    "& .MuiGrid-item": {
      display: "flex",
    },
  }
  
  const boxStylesContainer = {
    height: "100%",
    alignContent: "center",
    borderRadius: "20px",
    // backgroundColor: "mediumaquamarine",
  }

  const stackStylesWrapper = {
    margin: "20px 0"
  }

  const ratingStylesBox = {
    border: "1px solid #C5A572",
    borderRadius: "50px",
    flex: "0 0 calc(33.333% - 25px)",
    backgroundColor: "transparent",
    padding: "30px",
  }

  const boxStylesWrapper = {
    width: "60%"
  }

  return (
    <>
      {/*Grids */}
      <Grid
        container
        rowSpacing={4}
        columnSpacing={2}
        sx={gridStylesContainer}
      >
        
        <Grid
          sx={{
            textAlign: {
              xs: "center",
              sm: "center",
              md: "center",
              lg: "start",
              xl: "start",
            }
          }}
          size={{
            xs: 12,
            sm: 12,
            md: 12,
            lg: index === true ? 5 : 12,
            xl: index === true ? 6 : 12
          }}
          item
        >

          <Box
            sx={boxStylesContainer}
          >

            {/* Hero Text Wrapper */}
            <Stack
              direction={"column"}
              spacing={
                index !== true ? 2 :
                null
              }
              sx={stackStylesWrapper}
            >
                
              <Text
                text={title}
                variant={
                  index === true ? "indexHeroH1" :
                  "otherPagesH1"
                }/>

                <Box
                  sx={{
                    textAlign : index === false ? "justify" : null 
                  }}>
                <Text
                  text={subtitle}
                  variant={
                    index === true ? "indexHeroH2" :
                  "otherPagesH2"}/>
                </Box>


            </Stack>

            {index === true ? (
              <>
                {/* Hero Buttons Wrapper*/}
                <Stack
                  // backgroundColor="mediumaquamarine"
                  direction={{
                    xs: "column",
                    sm: "column",
                    md: "column",
                    lg: "column",
                    xl: "row"
                  }}
                  spacing={2}
                >
                    
                  <ButtonComp
                    variant="contained"
                    noElevate={true}
                    noRipple={true}
                    label={
                      isScheduleLoading ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <CircularProgress size={20} sx={{ color: "#FDF9F3" }} />
                          <span>{isClinicPersonnel ? "Manage Appointment" : "Schedule Appointment"}</span>
                        </Box>
                      ) : (isClinicPersonnel ? "Manage Appointment" : "Schedule Appointment")
                    }
                    color="light"
                    paddings="default"
                    background="default"
                    font="default"
                    weight="default"
                    transform="default"
                    selected={true}
                    keys={false}
                    hoverTextColor="default"
                    hoverBackground="default"
                    hoverBorder="none"
                    noRead={isScheduleLoading}
                    type="default"
                    justify="default"
                    destination="none"
                    open={true}
                    buttonId="scheduleAppointment"
                    clickPass={handleScheduleAppointment}/>

                  <ButtonComp
                    variant="contained"
                    noElevate={true}
                    noRipple={true}
                    label={
                      isPortfolioLoading ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <CircularProgress size={20} sx={{ color: "#FDF9F3" }} />
                          <span>View Dental Portfolio</span>
                        </Box>
                      ) : "View Dental Portfolio"
                    }
                    color="gold"
                    paddings="default"
                    background="none"
                    font="default"
                    weight="default"
                    transform="default"
                    border="light"
                    selected={true}
                    keys={false}
                    hoverTextColor="default"
                    hoverBackground="default"
                    hoverBorder="dark"
                    noRead={isPortfolioLoading}
                    type="default"
                    justify="default"
                    destination="/about"
                    open={true}
                    buttonId="viewPortfolio"/>
                          
                </Stack>
              </>
            ) : (
              <></>
            )}
                      
          </Box>

        </Grid>

        {index === true ? (
        <>
          <Grid size={{ xs: 12, sm: 12, md: 7, lg: 7, xl: 6 }} item
            sx={{
              display: {
                xs: "none",
                sm: "none",
                md: "none",
                lg: "block",
                xl: "block",
              },
            }}
          >

              <Box
                sx={boxStylesContainer}
              >

              {/* Hero Ratings Wrapper */}
              <Box
                sx={{
                          width: "100%",
                          margin: "0 auto",
                          display: "flex",  
                          justifyContent: "space-evenly",
                          flexDirection: "column",
                          // backgroundColor: "gray",
                        }}>

                        {/* Rating 1 Card */}
                        <Card
                          elevation={5}
                          sx={ratingStylesBox}>
                            
                          <CardContent>

                            <Stack spacing={1}>
                              <Rating value={5} readOnly />
                            </Stack>

                            <Box
                              sx={{
                                backgroundColor: "#C5A572",
                                margin: "30px 0px 0px 0px",
                                padding: "10px 20px",
                                borderRadius: "30px",
                              }}>

                            <Stack direction="column" spacing={1}
                              sx={{
                                alignItems: "end",
                              }}>

                              <Box
                                sx={{
                                  width: "100%",
                                }}>
                              <Typography
                                variant="body1"
                                sx={{
                                  padding: "10px 0",
                                  color: "#FEFEFE",
                                  fontWeight: "300",
                                }}
                              >“Very friendly and super approachable staff. Very flexible rates. It’s always great to invest your teeth in people you can trust. Helping me have a great smile boosts my confidence every day.”
                              </Typography>
                              </Box>

                                <Typography
                                  gutterBottom
                                  variant="h3"
                                  component="div"
                                  sx={{
                                    fontSize: "14px",
                                    color: "#FEFEFE",
                                    fontWeight: "300",
                                  }}
                                >-Dr. Eugene</Typography>
                              
                            </Stack>
                            </Box>

                          </CardContent>
                            
                        </Card>
                        
                      </Box>

                  
                </Box>

          </Grid>
        </>
        ) : (null)}
        
      </Grid>
    </>
  )

}

export default Hero;